import { CopyIcon } from "lucide-react";
import React from "react";
import { UploadProfile } from "./modal/UploadProfileModal";
import { DefaultText, H2, H3, LightText2 } from "@/common/ui/Text";
import { getNestedValue } from "@/utils/helper/form";
import moment from "moment";
import { FORMATE } from "@/utils/constant/global.constant";
import CustomLink from "@/common/ui/CustomLink";
import { TruncatedLine } from "@/common/ui/Truncated";
import { formateCurrency } from "@/utils/helper/string";
import { ProfileIcon } from "@/common/input/SelectBoxFromApi";
import SensitiveField from "@/components/common/SensitiveField";

interface ProfileViewProps {
  formConfig: any;
  data: any;
  nameKey: string;
  profilePicKey: string;
  openModal: (modalKey: string, props?: any) => void;
  updateAndFetchProfile: (data: any) => Promise<void>;
}

export const ViewField: React.FC<{ label: string; value: any; fieldType?: string; copyable?: boolean; C?: any; viewType?: string; field?: string }> = ({
  label,
  value,
  fieldType,
  copyable = false,
  C,
  viewType,
  field,
}) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
  };
  const getValues = () => {
    if (fieldType === "phone") {
      if (!value?.country_phone_code) {
        return value;
      }
      return `+${value?.country_phone_code} ${value?.phoneNo}`;
    } else if (fieldType === "select-from-api") {
      return `${value?.label || value}`;
    } else if (fieldType === "number-with-currency") {
      if (!value || value === "-") return "-";
      const currencyValue = formateCurrency(value);
      return ` ${value[0] ? value[0] : ""} ${currencyValue}`;
    } else if (fieldType === "radio") {
      return value === 1 ? "Yes" : "No";
    } else if (fieldType === "date") {
      return value && moment(value).isValid() ? moment(value).format(FORMATE.DATE) : "-";
    } else {
      return typeof value === "object" ? "OBJECT" : value;
    }
  };

  // Check if this is a sensitive field (CTC, salary-related)
  const isSensitiveField = field === "ctc" || field === "lastDrawnSalary" || field?.toLowerCase().includes("salary");
  const shouldShowSensitive = isSensitiveField && fieldType === "number-with-currency" && value && value !== "-";

  // console.log({ value, fieldType, label, viewType });

  return (
    <div className="flex flex-col gap-1">
      <label>
        <DefaultText text={label} />
      </label>
      <div className="flex items-center gap-2">
        {viewType === "profile" ? (
          <div className="flex items-center justify-between gap-1">
            {value?.profilePic ? (
              <img
                src={value.profilePic}
                alt={value.profileText}
                className="h-5 w-5 rounded-full object-cover object-center"
                style={{ minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }}
              />
            ) : (
              <ProfileIcon className="!border" text={value.profileText} />
            )}
            <LightText2 text={value?.label || value?.name} className="text-wrap" />
          </div>
        ) : (
          <>
            {fieldType === "upload-document-file" ? (
              <div className="flex items-center justify-between">
                {value?.url ? <CustomLink link={{ text: value?.name, value: value?.url }} isExternal className="text-13 !p-0" /> : "-"}
              </div>
            ) : fieldType === "textarea" ? (
              <TruncatedLine value={value} maxLength={50} lines={3} className="font-normal !leading-normal text-13" />
            ) : shouldShowSensitive ? (
              <SensitiveField
                value={value}
                renderValue={(val) => {
                  // Handle array format [currencyId, amount]
                  const amountStr = Array.isArray(val) && val.length > 1 
                    ? (typeof val[1] === "number" ? String(val[1]) : (typeof val[1] === "string" ? val[1] : String(val)))
                    : (typeof val === "string" ? val : String(val));
                  const currencyValue = formateCurrency(amountStr);
                  const currencySymbol = Array.isArray(val) && val[0] ? val[0] : "";
                  return <LightText2 text={`${currencySymbol} ${currencyValue}`.trim()} className="text-wrap" />;
                }}
                className="text-wrap"
              />
            ) : (
              <LightText2 text={getValues()} className="text-wrap" />
            )}
          </>
        )}

        {copyable && (
          <button onClick={handleCopy} className="p-1 hover:bg-gray-100 rounded-full">
            <CopyIcon className="w-4 h-4 text-gray-500" />
          </button>
        )}

        {C && C}
      </div>
    </div>
  );
};

const ProfileView: React.FC<ProfileViewProps> = ({ formConfig, data, nameKey, profilePicKey, openModal, updateAndFetchProfile }) => {
  const handleOpenProfileModal = () =>
    openModal("uploadProfileImage", { profileUrl: data?.[profilePicKey], profile_key: profilePicKey, updateAndFetchProfile, openModal });

  return (
    <div className="bg-white rounded-lg">
      {/* Header with Profile Image */}
      <div className="flex flex-col">
        {data[nameKey] && <UploadProfile name={data[nameKey]} profileUrl={data[profilePicKey]} handleOnClick={handleOpenProfileModal} />}
        <div className="px-4 flex gap-x-2 items-center">
          {data?.orgName && <H3 text={data.orgName} className="text-base" />}
          {data?.orgId && <div className="size-1 rounded-full bg-borderColor1"></div>}
          {data?.orgId && <LightText2 text={data.orgId} />}
        </div>
        <div className=" px-4">{data.address && <LightText2 text={data.address} />}</div>
      </div>
      <div className=" my-4 rounded-llg border border-stroke-light">
        <div className=" p-5 pt-[18px] border-b-[1px] border-stroke-light">
          <H2 text="Overview" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
          {formConfig.sections.map((section: any) => {
            return section.widgets.map((widget: any) => {
              if (widget?.hideOnViewMode) return null;
              const prefillField = widget?.viewField || widget?.apiField;
              let fieldValue = prefillField ? getNestedValue(data, prefillField) : undefined;
              if (prefillField === "org_type" && fieldValue === "Others") {
                fieldValue = prefillField ? getNestedValue(data, "org_others") : undefined;
              }
              return (
                <div className="px-6 py-4 overflow-hidden" key={widget.field}>
                  <ViewField label={widget.label} value={fieldValue || "-"} fieldType={widget.widgetType} />
                </div>
              );
            });
          })}
        </div>
      </div>
    </div>
  );
};

export default ProfileView;
